import React, {
    useEffect,
    useCallback,
    useState,
    createContext,
    useContext,
} from "react";
import { FilterButton } from "./Button";
import PropTypes from "prop-types";

// Similar to https://reach.tech/disclosure/ but simpler
// Keep them API compatible in case we want to switch over to using Reach
// disclosure instead

const initialState = {
    isControlled: false,
    shouldShow: false,
    toggleShouldShow: () => {},
};
const DisclosureContext = createContext(initialState);

export function Disclosure({ open = null, children, id }) {
    const [shouldShow, setShouldShow] = useState(open);
    const [isControlled, setIsControlled] = useState(open !== null);

    const toggleShouldShow = useCallback((e) => {
        e.stopPropagation();
        setShouldShow((show) => !show);
    }, []);

    useEffect(() => {
        setShouldShow(open);
    }, [open]);

    useEffect(() => {
        !isControlled && open !== null && setIsControlled(true);
    }, [open, isControlled]);

    const context = {
        isControlled,
        shouldShow,
        toggleShouldShow,
        panelId: `${id}-panel`,
    };

    return (
        <DisclosureContext.Provider value={context}>
            {children}
        </DisclosureContext.Provider>
    );
}
Disclosure.propTypes = {
    open: PropTypes.bool,
    children: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
};
Disclosure.defaultProps = {
    open: null,
};

const useDisclosure = () => useContext(DisclosureContext);

export function DisclosureButton({
    className = "",
    as: Component = FilterButton,
    children,
    ...rest
}) {
    const {
        isControlled,
        shouldShow,
        toggleShouldShow,
        panelId,
    } = useDisclosure();

    return (
        <Component
            className={className}
            aria-expanded={shouldShow}
            aria-controls={panelId}
            onClick={!isControlled ? toggleShouldShow : null}
            {...rest}
        >
            {children}
        </Component>
    );
}
DisclosureButton.propTypes = {
    as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    className: PropTypes.string,
    children: PropTypes.node,
};
DisclosureButton.defaultProps = {
    as: FilterButton,
    className: "",
    children: null,
};

export function DisclosurePanel({ className = "", children }) {
    const { shouldShow, panelId } = useDisclosure();
    return (
        shouldShow && (
            <div id={panelId} className={className}>
                {children}
            </div>
        )
    );
}
DisclosurePanel.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};
DisclosurePanel.defaultProps = {
    className: "",
};
